'use client';
import styles from './sportscard.module.scss';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import IconAt from '@/assets/icons/icon-at.svg';
import SXMLink from '@/shared-components/SXMLink';
import cn from 'classnames';
import { CSSProperties, ReactNode, useId, useState } from 'react';
import { PopoverWrapper } from '../PopoverWrapper';
import { ListenLive } from '@/ui-components/atoms/ListenLive/ListenLive';
import { ShowMore } from '@/ui-components/molecules/ShowMore/ShowMore';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';

type SportsCardThemeType = 'Carousel' | 'Hero';

export interface Team {
    name: string;
    city: string;
    link: Link;
    logo: ImageProps;
    channel?: string;
    isOnline?: boolean;
}

interface SportsCardProps {
    startTime: string;
    endTime: string;
    matchup: Team[];
    eventDateTime: Date;
    cta?: React.ReactNode;
    theme: SportsCardThemeType;
    style?: CSSProperties;
    className?: string;
    homeTeamColor?: string;
    awayTeamColor?: string;
    listenLiveLink?: Link;
    moreWaysTable?: ReactNode;
}

export default function SportsCard({
    eventDateTime,
    startTime,
    endTime,
    matchup,
    cta,
    theme = 'Hero',
    style = {},
    className,
    homeTeamColor,
    awayTeamColor,
    listenLiveLink,
    moreWaysTable,
}: SportsCardProps): React.JSX.Element {
    dayjs.extend(isBetween);

    const dt = dayjs(eventDateTime);
    const matchupDay = dt.format('dddd, MMMM D');
    const matchupTime = dt.format('h:mm A');
    const timezone = new Date(eventDateTime).toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
    const isNowBetweenStartAndEnd = (): boolean => {
        const nowMoment = dayjs();
        const startMoment = dayjs(startTime);
        const endMoment = dayjs(endTime);

        return nowMoment.isBetween(startMoment, endMoment);
    };
    const isGameLive = isNowBetweenStartAndEnd();

    const contentId = useId();

    const [showContent, setShowContent] = useState(false);

    const cardClasses = cn(styles.sportMatchupCard, className, {
        [styles.inCarousel]: theme === 'Carousel',
        [styles.homeTeamBorder]: !!homeTeamColor,
        [styles.awayTeamBorder]: !!awayTeamColor,
    });

    const cssStyles: React.CSSProperties = {
        ...style,
        ...{ '--border-bottom-color': homeTeamColor },
        ...{ '--border-top-color': awayTeamColor },
    };

    function renderTeamLabel(i: number, team: Team): React.JSX.Element | string {
        if (team.isOnline) {
            return 'ONLINE';
        }

        if (!team.channel) {
            return <></>;
        }

        return (
            <>
                {!isNaN(parseInt(`${team.channel}`)) ? 'CH.' : ''} {team.channel}
            </>
        );
    }

    return (
        <div className={cardClasses} style={cssStyles} data-cy="molecules-sports-card">
            <h3
                className={styles.matchupRow}
                aria-label={
                    matchup[1]
                        ? `${matchup[0].city} ${matchup[0].name} at ${matchup[1].city} ${matchup[1].name}`
                        : `${matchup[0].city} ${matchup[0].name}`
                }
            >
                {matchup.map((team, i) => (
                    <span key={i} className={styles.matchupCol}>
                        {theme === 'Hero' ? team.city : ''}
                        <span className={styles.teamName}>{team.name}</span>
                    </span>
                ))}
            </h3>

            <time className={`${styles.matchupDate} chromatic-ignore`}>
                <div className={styles.matchupDay}>{matchupDay}</div>
                <div className={styles.matchupTime}>
                    {matchupTime} {timezone}
                </div>
            </time>

            <div
                className={`${styles.matchupRow} ${styles.matchupChannels}`}
                role="img"
                aria-label={`Listen to ${matchup
                    .map((team) => `${team.name} on channel ${team.channel}`)
                    .join(' or ')}`}
            >
                {matchup.map((team, i) => (
                    <div key={i} className={styles.matchupCol}>
                        {renderTeamLabel(i, team)}
                    </div>
                ))}
            </div>

            <div className={`${styles.matchupRow} ${styles.matchupLogoGroup}`}>
                {matchup.map((team, i) => {
                    const matchupClasses = cn(styles.matchupCol, { [styles.singleMatchup]: matchup.length === 1 });

                    return (
                        <div className={matchupClasses} key={i}>
                            {team.link && team.link.href ? (
                                <SXMLink href={team.link.href}>
                                    <ImageWrapper
                                        src={team.logo.source}
                                        alt={team.logo.altText}
                                        optimizedHeight={team.logo.height}
                                        optimizedWidth={team.logo.width}
                                        maxWidth={60}
                                    />
                                </SXMLink>
                            ) : (
                                <ImageWrapper
                                    src={team.logo.source}
                                    alt={team.logo.altText}
                                    optimizedHeight={team.logo.height}
                                    optimizedWidth={team.logo.width}
                                />
                            )}
                        </div>
                    );
                })}
                {matchup.length > 1 && (
                    <div className={styles.matchupCol}>
                        <IconAt aria-hidden="true" role="presentation" className={styles.matchupIconAt} />
                    </div>
                )}
            </div>

            {theme === 'Carousel'
                ? cta && <div className={styles.matchupScheduleLink}>{cta}</div>
                : cta && !isGameLive && <div className={styles.matchupScheduleLink}>{cta}</div>}

            {theme === 'Hero' && isGameLive && (
                <div className={styles.listenLiveLink}>
                    <ListenLive link={listenLiveLink} />
                </div>
            )}

            {theme === 'Hero' && (
                <ShowMore
                    className={styles.moreInfo}
                    showText="More Ways to listen"
                    hideText="More Ways to listen"
                    color="var(--base-contrast-accent, #0000eb)"
                    ariaLabel="More Ways to listen"
                    activeToggleTextPosition="top"
                    id={contentId}
                    shownClassName={styles.moreInfoActive}
                    contentClassName={styles.moreInfoContent}
                    onClickHandler={() => setShowContent(true)}
                    isContentShown={showContent}
                >
                    <div className={styles.contentWrapper}>
                        <PopoverWrapper
                            contentPadding="24px 0 16px"
                            headerContent="More Ways to listen"
                            onClosePress={() => setShowContent(false)}
                        >
                            {moreWaysTable}
                        </PopoverWrapper>
                    </div>
                </ShowMore>
            )}
            {!!homeTeamColor && isGameLive && <div className={styles.borderBackground}></div>}
        </div>
    );
}
